import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, X, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PnL, money, fmt, StatCard } from "../../components/dashboard/Shared";
import { LiveBadge, ago, ConfirmModal } from "./mgrShared";
import { PeriodFilter } from "./client/PeriodFilter";
import { TradesTable, DailyTable, FundingTable } from "./client/TradesTable";
import { ProfitShareLedger } from "./client/ProfitShareLedger";
import { MessagePanel } from "./client/MessagePanel";

const TABS = [["trades", "Trades"], ["daily", "Day-wise"], ["share", "Profit Share"], ["funding", "Deposits / Withdrawals"], ["message", "Send Message"]];

const SourceCard = ({ label, s, accent }) => (
  <div className="card-dark p-4" data-testid={`source-card-${label.toLowerCase()}`}>
    <p className={`label-xs ${accent}`}>{label}</p>
    <div className="grid grid-cols-4 gap-2 mt-2 text-xs">
      <div><span className="text-gray-500">Trades</span><p className="tabular text-white">{s.trades}</p></div>
      <div><span className="text-gray-500">Profit</span><p className="tabular text-[#00E676]">{fmt(s.gross_profit)}</p></div>
      <div><span className="text-gray-500">Loss</span><p className="tabular text-[#FF3B30]">{fmt(s.gross_loss)}</p></div>
      <div><span className="text-gray-500">Net</span><p><PnL value={s.net} /></p></div>
    </div>
  </div>
);

const LivePositions = ({ positions, onClose, onCloseAll, closingId }) => {
  if (!positions?.length) return null;
  const total = positions.reduce((a, p) => a + (p.profit || 0), 0);
  return (
    <div className="card-dark p-4 mb-5" data-testid="live-positions-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap size={14} className="text-[#00E676]" />
          <p className="text-sm text-white">Live Open Positions <span className="text-gray-500">({positions.length})</span></p>
          <span className="ml-2 text-xs text-gray-500">Floating:</span><PnL value={Math.round(total * 100) / 100} />
        </div>
        <button data-testid="close-all-account-btn" onClick={onCloseAll}
          className="text-[11px] px-3 py-1.5 rounded-full border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors">
          Close All ({positions.length})
        </button>
      </div>
      <div className="overflow-x-auto -mx-1 px-1">
        <table className="w-full text-xs">
          <thead><tr className="text-gray-500 text-left">
            <th className="py-1.5 font-normal">Symbol</th><th className="py-1.5 font-normal">Side</th>
            <th className="py-1.5 font-normal text-right">Volume</th><th className="py-1.5 font-normal text-right">Open</th>
            <th className="py-1.5 font-normal text-right">Current</th><th className="py-1.5 font-normal text-right">Floating</th>
            <th className="py-1.5 font-normal text-right"></th>
          </tr></thead>
          <tbody>
            {positions.map((p) => (
              <tr key={p.id} data-testid={`live-pos-${p.id}`} className="border-t border-white/5">
                <td className="py-2 text-white">{p.symbol}</td>
                <td className={`py-2 uppercase ${p.type === "buy" ? "text-[#00E676]" : "text-[#FF3B30]"}`}>{p.type}</td>
                <td className="py-2 tabular text-right text-gray-300">{fmt(p.volume)}</td>
                <td className="py-2 tabular text-right text-gray-400">{p.open_price}</td>
                <td className="py-2 tabular text-right text-gray-400">{p.current_price ?? "—"}</td>
                <td className="py-2 text-right"><PnL value={Math.round((p.profit || 0) * 100) / 100} /></td>
                <td className="py-2 text-right">
                  <button data-testid={`close-pos-${p.id}`} onClick={() => onClose(p)} disabled={closingId === p.id}
                    className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-white/10 text-gray-300 hover:border-[#FF3B30]/50 hover:text-[#FF3B30] transition-colors disabled:opacity-40">
                    <X size={10} /> Close
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function ClientDetail() {
  const { accountId } = useParams();
  const [period, setPeriod] = useState({ preset: "today" });
  const [data, setData] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [tab, setTab] = useState("trades");
  const [msgWeek, setMsgWeek] = useState("");
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(null);
  const [confirm, setConfirm] = useState(null); // {type:'all'|'one', pos?}
  const [closing, setClosing] = useState(false);
  const [closingId, setClosingId] = useState(null);
  const prevOpen = useRef(null);

  const load = useCallback(async () => {
    try {
      const params = period.preset === "custom" ? { preset: "custom", from_day: period.from_day, to_day: period.to_day } : { preset: period.preset };
      const [t, l] = await Promise.all([
        api.get(`/manager/mt5-clients/${accountId}/trades`, { params }),
        api.get(`/manager/mt5-clients/${accountId}/profit-share`),
      ]);
      setData(t.data); setLedger(l.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load client"); }
  }, [accountId, period]);
  useEffect(() => { load(); }, [load]);

  // Real-time polling (~1s): live equity / balance / floating P&L + open positions.
  // A drop in open-position count means a trade just closed → reload the closed-trades view.
  useEffect(() => {
    let stopped = false; let timer;
    const tick = async () => {
      try {
        const { data: d } = await api.get(`/manager/mt5-clients/${accountId}/live`);
        if (stopped) return;
        setLive(d);
        if (prevOpen.current != null && d.open_positions != null && d.open_positions < prevOpen.current) {
          load();
        }
        if (d.open_positions != null) prevOpen.current = d.open_positions;
      } catch { /* keep last value */ }
      if (!stopped) timer = setTimeout(tick, 1000);
    };
    tick();
    return () => { stopped = true; clearTimeout(timer); };
  }, [accountId, load]);

  const refresh = async () => {
    setBusy(true);
    try { await api.post(`/manager/mt5-clients/${accountId}/refresh`); await load(); } finally { setBusy(false); }
  };

  const doCloseAll = async () => {
    setClosing(true);
    try {
      const { data: r } = await api.post(`/manager/mt5-clients/${accountId}/close-all`);
      toast.success(`Closed ${r.closed}/${r.total} position${r.total === 1 ? "" : "s"}`);
      prevOpen.current = 0; setConfirm(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to close positions"); }
    setClosing(false);
  };
  const doCloseOne = async (pos) => {
    setClosingId(pos.id);
    try {
      await api.post(`/manager/mt5-clients/${accountId}/close-position`, { position_id: String(pos.id) });
      toast.success(`Closed ${pos.symbol} ${pos.type}`); setConfirm(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to close position"); }
    setClosingId(null);
  };

  if (!data) return <div className="space-y-4"><div className="skeleton h-24" /><div className="skeleton h-64" /></div>;
  const a = data.account; const s = data.summary;
  const state = a.connection_status === "active" ? a.data_state : "offline";
  // Prefer real-time values from the live poll; fall back to the cached snapshot.
  const balance = live && !live.stale && live.balance != null ? live.balance : a.balance;
  const equity = live && !live.stale && live.equity != null ? live.equity : a.equity;
  const floating = live && !live.stale && live.floating_pnl != null ? live.floating_pnl : a.floating_pnl;
  const openN = live && !live.stale && live.open_positions != null ? live.open_positions : a.open_positions;
  const positions = live?.positions || [];

  return (
    <div data-testid="client-detail-page">
      <Link to="/manager/active" className="text-xs text-gray-500 hover:text-white inline-flex items-center gap-1 mb-4" data-testid="back-to-clients"><ArrowLeft size={12} /> Back to clients</Link>
      <div className="card-dark p-5 mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl text-white" data-testid="client-name">{a.client_name}</h1>
          <p className="text-sm text-gray-500">{a.client_email}{data.client?.phone ? ` · ${data.client.phone}` : ""}</p>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
            <span className="text-gray-300">{a.broker}</span><span className="tabular text-gray-400">{a.login}</span><span className="text-gray-600">{a.server}</span>
            <LiveBadge state={state} /><span className="text-gray-600">{ago(live && !live.stale ? live.synced_at || a.last_seen : a.last_seen)}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${a.trading_paused ? "text-[#FFD600] bg-[#FFD600]/10" : "text-[#00E676] bg-[#00E676]/10"}`}>{a.trading_paused ? "trading paused" : "trading active"}</span>
            {a.chartzfx_trading_enabled === false && <span className="text-[10px] px-1.5 py-0.5 rounded uppercase text-[#FF3B30] bg-[#FF3B30]/10">chartzfx off</span>}
          </div>
        </div>
        <div className="flex items-start gap-6">
          <div className="text-right"><p className="label-xs">Balance</p><p className="tabular text-white text-lg" data-testid="hdr-balance">{balance != null ? money(balance) : "—"}</p></div>
          <div className="text-right"><p className="label-xs">Equity</p><p className="tabular text-[#00F0FF] text-lg" data-testid="hdr-equity">{equity != null ? money(equity) : "—"}</p></div>
          <div className="text-right"><p className="label-xs">Floating</p><p className="text-lg" data-testid="hdr-floating">{floating != null ? <PnL value={floating} /> : <span className="text-gray-600">N/A</span>}</p></div>
          <div className="text-right"><p className="label-xs">Open</p><p className="tabular text-white text-lg" data-testid="hdr-open">{openN ?? "—"}</p></div>
          <div className="text-right"><p className="label-xs">Active Lot</p><p className="tabular text-white text-lg">{a.active_execution_lot != null ? fmt(a.active_execution_lot) : "RISK%"}</p></div>
          <div className="flex flex-col gap-2">
            <button data-testid="client-refresh-btn" onClick={refresh} disabled={busy} className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2 disabled:opacity-50"><RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Sync</button>
            {openN > 0 && (
              <button data-testid="header-close-all-btn" onClick={() => setConfirm({ type: "all" })}
                className="px-3 py-1.5 text-xs rounded-lg font-medium bg-[#FF3B30] text-white hover:bg-[#ff5147] transition-colors">Close All</button>
            )}
          </div>
        </div>
      </div>

      <LivePositions positions={positions} closingId={closingId}
        onClose={(p) => setConfirm({ type: "one", pos: p })}
        onCloseAll={() => setConfirm({ type: "all" })} />

      <div className="mb-4"><PeriodFilter value={period} onChange={setPeriod} /></div>
      <p className="text-[11px] text-gray-600 mb-3" data-testid="range-label">Showing closed trades {data.range.from} → {data.range.to}</p>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
        <StatCard label="Trades" value={s.trades} sub={`${s.wins}W / ${s.losses}L`} testId="sum-trades" />
        <StatCard label="Win Rate" value={`${s.win_rate}%`} testId="sum-winrate" />
        <StatCard label="Lots" value={fmt(s.lots)} testId="sum-lots" />
        <StatCard label="Gross Profit" value={money(s.gross_profit)} accent="text-[#00E676]" testId="sum-profit" />
        <StatCard label="Gross Loss" value={money(s.gross_loss)} accent="text-[#FF3B30]" testId="sum-loss" />
        <StatCard label="Net P/L" value={<PnL value={s.net} />} testId="sum-net" />
        <StatCard label="SL Hits" value={s.sl_hits} accent="text-[#FF3B30]" testId="sum-sl" />
        <StatCard label="TP Hits" value={s.tp_hits} accent="text-[#00E676]" testId="sum-tp" />
      </div>
      <div className="grid md:grid-cols-2 gap-3 mb-5">
        <SourceCard label="ChartzFx Signals" s={data.by_source.chartzfx} accent="text-[#7000FF]" />
        <SourceCard label="Client Manual" s={data.by_source.manual} accent="text-[#FFD600]" />
      </div>

      <div className="flex gap-1 border-b border-white/10 mb-4 overflow-x-auto">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === k ? "border-[#00F0FF] text-[#00F0FF]" : "border-transparent text-gray-500 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      <div className="card-dark p-4">
        {tab === "trades" && <TradesTable trades={data.trades} />}
        {tab === "daily" && (data.daily.length ? <DailyTable daily={data.daily} /> : <p className="text-sm text-gray-500 py-8 text-center">No trades in this period.</p>)}
        {tab === "share" && <ProfitShareLedger accountId={accountId} ledger={ledger} onChange={setLedger} onMessage={(w) => { setMsgWeek(w.week_start); setTab("message"); }} />}
        {tab === "funding" && <FundingTable funding={data.funding} />}
        {tab === "message" && <MessagePanel accountId={accountId} client={data.client} weeks={ledger?.weeks} initialWeek={msgWeek} />}
      </div>

      <ConfirmModal
        open={!!confirm}
        title={confirm?.type === "one" ? "Close this position?" : "Close all open positions?"}
        body={confirm?.type === "one"
          ? `This will immediately close ${confirm?.pos?.symbol} ${confirm?.pos?.type?.toUpperCase()} (${fmt(confirm?.pos?.volume)} lots) at market price.`
          : `This will immediately close ALL ${openN || positions.length} open position${(openN || positions.length) === 1 ? "" : "s"} for ${a.client_name} at market price. This cannot be undone.`}
        confirmLabel={confirm?.type === "one" ? "Close Position" : "Close All"}
        busy={closing || closingId != null}
        onCancel={() => setConfirm(null)}
        onConfirm={() => (confirm?.type === "one" ? doCloseOne(confirm.pos) : doCloseAll())}
      />
    </div>
  );
}
